import {
    Notes,
    PointedSyllable,
    PointedText,
    PointedWord,
    TextData,
} from "../models";
import { CANTICLES } from "./canticles";
import { PSALMS_1928 } from "./data-1928";
import { GLORIA_PATRI } from "./gloria-patri";
import { HYPHENATION } from "./hyphenation";
import { getTone } from "./tones";

export type PsalmRef =
    | { type: "full"; number: number }
    | {
          type: "section";
          number: number;
          firstVerse: number;
          lastVerse: number;
      };

export type CanticleID =
    | "venite"
    | "te deum"
    | "benedictus"
    | "magnificat"
    | "nunc dimittis"
    | "pascha nostrum"
    | "benedictus es"
    | "quicunque vult";

export const getPsalmRefFromText = (refText: string): PsalmRef => {
    if (refText.includes(":")) {
        const parts = refText.split(/[:-]/g).map((part) => parseInt(part));
        return {
            type: "section",
            number: parts[0],
            firstVerse: parts[1],
            lastVerse: parts[2],
        };
    } else {
        return { type: "full", number: parseInt(refText) };
    }
};

export const getTextFromPsalmRef = (psalmRef: PsalmRef) => {
    if (psalmRef.type === "full") {
        return `Psalm ${psalmRef.number}`;
    } else {
        return `Psalm ${psalmRef.number}:${psalmRef.firstVerse}-${psalmRef.lastVerse}`;
    }
};

const normalizeText = (text: string) => {
    return text
        .replace(" * ", " ")
        .replace(/\./g, "")
        .replace(/:/g, "")
        .replace(/,/g, "")
        .replace(/;/g, "")
        .replace(/\!/g, "")
        .replace(/\?/g, "")
        .replace(/\~/g, "")
        .replace(/\'/g, "")
        .replace(/\(/g, "")
        .replace(/\)/g, "")
        .toLocaleLowerCase();
};

export const getWords = (rawPsalms: any) => {
    const words = new Set<string>([]);

    rawPsalms.forEach((psalm: any) => {
        psalm.lines.forEach((line: string) => {
            normalizeText(line)
                .split(" ")
                .forEach((word) => {
                    words.add(word);
                });
        });
    });

    const result: any = [];
    words.forEach((word) => {
        result.push([word, word]);
    });

    return result;
};

const getHyphenDictionary = () => {
    const result: { [word: string]: string } = {};

    HYPHENATION.forEach(([word, hyphenation]) => {
        result[word] = hyphenation;
    });

    return result;
};

const getSyllables = (
    dictionary: { [word: string]: string },
    word: string
): string[] => {
    const pattern = dictionary[normalizeText(word)];

    let patternPointer = 0;
    let wordPointer = 0;

    const result: string[] = [];
    let curr = "";

    while (wordPointer < word.length) {
        if (pattern[patternPointer] === "-") {
            result.push(curr);
            curr = "";
            patternPointer++;
        } else if (pattern[patternPointer] === "_") {
            curr += "-";
            result.push(curr);
            curr = "";
            patternPointer++;
            wordPointer++;
        } else if (
            pattern[patternPointer] === word[wordPointer].toLocaleLowerCase()
        ) {
            curr += word[wordPointer];
            patternPointer++;
            wordPointer++;
        } else {
            curr += word[wordPointer];
            wordPointer++;
        }
    }

    return result.concat([curr]);
};

export const getPsalmsDatabase = (rawPsalms: any): TextData[] => {
    const hyphenDictionary = getHyphenDictionary();

    const result: TextData[] = [];

    rawPsalms.forEach((psalm: any) => {
        const lines = psalm.lines.map((line: string) => {
            const halves = line.split("*");

            return halves.map((half) => {
                const words = half.trim().split(" ");

                return words
                    .map((word) =>
                        getSyllables(hyphenDictionary, word).join("_")
                    )
                    .join(" ");
            });
        });

        result.push({
            id: `psalm${psalm.psalm}`,
            lines,
        });
    });

    return result;
};

export const getPsalmSyllables = (rawPsalms: any): string[][][][][] => {
    const hyphenDictionary = getHyphenDictionary();

    return rawPsalms.map((psalm: any) => {
        return psalm.lines.map((line: string) => {
            const halves = line.split("*");

            return halves.map((half) => {
                const words = half.trim().split(" ");

                return words.map((word) =>
                    getSyllables(hyphenDictionary, word)
                );
            });
        });
    });
};

export const getEnding = (rawHalf: string) => {
    const half = rawHalf.split(" ").map((word) => word.split("_"));
    let pointer = half.length;
    let syllables = 0;

    while (syllables < 6 && pointer > 0) {
        pointer--;
        syllables += half[pointer].length;
    }

    const ending = half
        .slice(pointer)
        .map((syllables) => syllables.join("_"))
        .join(" ");

    return normalizeText(ending);
};

export const getEndings = () => {
    const endings: {
        [ending: string]: { tagged: string; examples: string[] };
    } = {};

    PSALMS_1928.forEach((text) => {
        text.lines.forEach((line) => {
            line.forEach((rawHalf) => {
                const ending = getEnding(rawHalf);

                if (!endings[ending]) {
                    endings[ending] = {
                        tagged: ending,
                        examples: [rawHalf],
                    };
                } else {
                    endings[ending].examples.push(rawHalf);
                }
            });
        });
    });

    return endings;
};

export const getHalfEmphasisPoints = (
    half: string,
    emphasisDb?: {
        [ending: string]: { tagged: string; examples: string[] };
    }
) => {
    if (half.includes("/")) {
        // Already has stress marks
        const emphasisPoints: number[] = [];

        half.replace(/_/g, " ")
            .split(" ")
            .reverse()
            .forEach((syllable, syllableIndex) => {
                if (syllable[0] === "/") {
                    emphasisPoints.push(syllableIndex + 1);
                }
            });

        return emphasisPoints;
    } else {
        // Needs stress marks based on the ending
        const ending = getEnding(half);

        const emphasisPoints: number[] = [];

        const finalEmphasisDatabase = emphasisDb ?? {};

        try {
            finalEmphasisDatabase[ending].tagged
                .replace(/_/g, " ")
                .split(" ")
                .reverse()
                .forEach((syllable, syllableIndex) => {
                    if (syllable[0] === "/") {
                        emphasisPoints.push(syllableIndex + 1);
                    }
                });
        } catch {
            throw new Error(`ENDING BROKEN FOR [${ending}]`);
        }

        if (!finalEmphasisDatabase[ending].tagged.includes("/")) {
            throw new Error(`No emphasis points included for [${ending}]`);
        }

        return emphasisPoints;
    }
};

export const getNotesFromString = (noteString: string): string[] => {
    const notes: string[] = [];

    Array.from(noteString.replace(".", "").replace("/", "")).forEach((char) => {
        if (char === "b") {
            notes[notes.length - 1] += "b";
        } else {
            notes.push(char);
        }
    });

    return notes;
};

export const getPointedHalf = (
    half: string,
    recitingNote: number,
    ending: string[],
    intonation: string[],
    emphasisDb?: {
        [ending: string]: { tagged: string; examples: string[] };
    }
): PointedWord[] => {
    // Compute all the possible tone endings (at most 4)
    let toneEndings: string[][] = [[]];

    ending.forEach((syllable) => {
        if (syllable[syllable.length - 1] === "?") {
            toneEndings = toneEndings.concat(
                toneEndings.map((ending) => ending.concat([syllable]))
            );
        } else if (syllable[syllable.length - 1] === "~") {
            toneEndings = toneEndings
                .filter(
                    (toneEnding) => toneEnding.length === toneEndings[0].length
                )
                .concat(toneEndings.map((ending) => ending.concat([syllable])))
                .concat(
                    toneEndings.filter(
                        (toneEnding) =>
                            toneEnding.length > toneEndings[0].length
                    )
                );
        } else {
            toneEndings.forEach((ending) => ending.push(syllable));
        }
    });

    // Work out which ending matches the half best
    const halfEmphasisPoints = getHalfEmphasisPoints(half, emphasisDb);
    let bestEnding: string[] = [];
    let bestEndingScore = -1;

    toneEndings.forEach((toneEnding) => {
        const endingEmphasisPoints = toneEnding
            .slice()
            .reverse()
            .map((syllable, syllableIndex) =>
                syllable[0] === "/" ? syllableIndex + 1 : 0
            )
            .filter((point) => point > 0);

        const score = endingEmphasisPoints.filter((point) =>
            halfEmphasisPoints.includes(point)
        ).length;

        if (score > bestEndingScore) {
            bestEnding = toneEnding;
            bestEndingScore = score;
        }
    });

    bestEnding = bestEnding.map((note) =>
        note.replace("/", "").replace("?", "").replace("~", "")
    );

    // Don't use the intonation if there aren't enough syllables
    if (
        intonation.length + bestEnding.length >
        half.replace(/ /g, "_").split("_").length
    ) {
        intonation = [];
    }

    // Generate the pointed words
    const totalSyllables = half.replace(/_/g, " ").split(" ").length;
    let syllableIndex = 0;

    let dotUsed = false;

    return half
        .replace(/\//g, "")
        .split(" ")
        .map((word) => {
            const pointedWord: PointedWord = { syllables: [] };

            word.split("_").forEach((syllable) => {
                let noteString = `${
                    syllableIndex < intonation.length
                        ? intonation[syllableIndex]
                        : recitingNote
                }`;
                let dotBefore = false;
                if (syllableIndex >= totalSyllables - bestEnding.length) {
                    if (!dotUsed) {
                        dotBefore = true;
                        dotUsed = true;
                    }
                    noteString =
                        bestEnding[
                            syllableIndex - (totalSyllables - bestEnding.length)
                        ];
                }

                pointedWord.syllables.push({
                    text: syllable,
                    notes: getNotesFromString(noteString),
                    long: noteString.includes("."),
                    dotBefore,
                });

                syllableIndex++;
            });

            return pointedWord;
        });
};

export interface OutstandingEnding {
    ending: string;
    verse: [string, string];
}
export const getOutstandingEndingsForPsalm = (
    refString: string,
    emphasisDb?: {
        [ending: string]: { tagged: string; examples: string[] };
    }
): OutstandingEnding[] => {
    const psalmRef = getPsalmRefFromText(refString);

    const id = `psalm${psalmRef.number}`;

    const textData = PSALMS_1928.find((td) => td.id === id);

    if (!textData) {
        throw new Error(`Could not find the text for ${id}`);
    }

    const lines =
        psalmRef.type === "full"
            ? textData.lines
            : textData.lines.slice(psalmRef.firstVerse - 1, psalmRef.lastVerse);

    const endings: OutstandingEnding[] = [];

    lines.forEach((line) => {
        try {
            getPointedHalf(line[0], 5, [], [], emphasisDb);
        } catch (error) {
            endings.push({ ending: getEnding(line[0]), verse: line });
        }

        try {
            getPointedHalf(line[1], 5, [], [], emphasisDb);
        } catch {
            endings.push({ ending: getEnding(line[1]), verse: line });
        }
    });

    return endings;
};

export const getPointedText = (
    lines: [string, string][],
    toneId: string,
    gloriaPatri: boolean,
    intoneEveryVerse: boolean,
    emphasisDb?: {
        [ending: string]: { tagged: string; examples: string[] };
    }
) => {
    const tone = getTone(toneId);

    if (gloriaPatri) {
        lines = lines.concat(GLORIA_PATRI);
    }

    return {
        clef: tone.clef,
        verses: lines.map((line, lineIndex) => {
            return {
                firstHalf: getPointedHalf(
                    line[0],
                    tone.recitingNote,
                    tone.mediant,
                    lineIndex === 0 || intoneEveryVerse ? tone.intonation : [],
                    emphasisDb
                ),
                secondHalf: getPointedHalf(
                    line[1],
                    tone.secondRecitingNote ?? tone.recitingNote,
                    tone.ending,
                    [],
                    emphasisDb
                ),
            };
        }),
    };
};

const getPointedAntiphonPart = (
    text: string,
    intonation: Notes,
    recitingNote: number,
    ending: Notes,
    firstCap: boolean,
    startWithQBar: boolean
) => {
    const words = text.split(" ");

    let totalSyllables = 0;
    words.forEach((word) => {
        totalSyllables += word.split("_").length;
    });

    const pointedWords: PointedWord[] = [];

    let syllableIndex = 0;
    words.forEach((word, wordIndex) => {
        const syllables: PointedSyllable[] = [];

        (firstCap &&
        (wordIndex === 0 || (wordIndex === 1 && words[0].length === 1))
            ? word.toLocaleUpperCase()
            : word
        )
            .split("_")
            .forEach((syllable) => {
                if (
                    syllableIndex < intonation.length &&
                    totalSyllables >= intonation.length + ending.length
                ) {
                    // Use intonation for note
                    syllables.push({
                        text: syllable,
                        notes: getNotesFromString(intonation[syllableIndex]),
                        long: false,
                        dotBefore: false,
                    });
                    syllableIndex++;
                } else {
                    const endingIndex =
                        syllableIndex - (totalSyllables - ending.length);

                    if (endingIndex >= 0) {
                        // Use ending for note
                        syllables.push({
                            text: syllable,
                            notes: getNotesFromString(ending[endingIndex]),
                            long:
                                ending[endingIndex][
                                    ending[endingIndex].length - 1
                                ] === ".",
                            dotBefore: endingIndex === 0,
                            qBarBefore: syllableIndex === 0 && startWithQBar,
                        });
                        syllableIndex++;
                    } else {
                        // Use reciting note
                        syllables.push({
                            text: syllable,
                            notes: [`${recitingNote}`],
                            long: false,
                            dotBefore: false,
                            qBarBefore: syllableIndex === 0 && startWithQBar,
                        });
                        syllableIndex++;
                    }
                }
            });

        pointedWords.push({ syllables });
    });

    return pointedWords;
};

export const getPointedAntiphon = (
    text: string,
    toneId: string
): PointedText => {
    const parts = text
        .replace(/-/g, "_")
        .replace(/ \* /g, " ")
        .replace(/~/g, "-")
        .split("|");

    const initParts = parts.slice(0, parts.length - 1);
    const finalPart = parts[parts.length - 1];

    const tone = getTone(toneId);

    const firstHalf: PointedWord[] = [];

    initParts.forEach((part, index) => {
        const pointedWords = getPointedAntiphonPart(
            part,
            index === 0 ? tone.intonation : [],
            tone.recitingNote,
            tone.mediant,
            index === 0,
            index !== 0
        );

        firstHalf.push(...pointedWords);
    });

    return {
        clef: tone.clef,
        verses: [
            {
                firstHalf,
                secondHalf: getPointedAntiphonPart(
                    finalPart,
                    [],
                    tone.secondRecitingNote ?? tone.recitingNote,
                    tone.ending,
                    false,
                    false
                ),
            },
        ],
    };
};

export const getPointedPsalm = (
    psalmRef: PsalmRef,
    toneId: string,
    gloriaPatri: boolean
): PointedText => {
    const id = `psalm${psalmRef.number}`;

    const textData = PSALMS_1928.find((td) => td.id === id);

    if (!textData) {
        throw new Error(`Could not find the text for ${id}`);
    }

    const lines =
        psalmRef.type === "full"
            ? textData.lines
            : textData.lines.slice(psalmRef.firstVerse - 1, psalmRef.lastVerse);

    return getPointedText(lines, toneId, gloriaPatri, false);
};

export const getPointedCanticle = (id: CanticleID, toneId: string) => {
    return getPointedText(
        CANTICLES[id].lines,
        toneId,
        id !== "te deum",
        id !== "venite" &&
            id !== "te deum" &&
            id !== "benedictus es" &&
            id !== "quicunque vult"
    );
};

export const getEasierTone = (toneId: string) => {
    switch (toneId[0]) {
        case "1":
            return "1a.1";
        case "2":
            return "2.1";
        case "3":
            return "3a.4";
        case "4":
            return "4.7";
        case "5":
            return "5.1";
        case "6":
            return "6a";
        case "7":
            return "7.5";
        case "8":
            return "8.1";
        case "T":
            return "Tonus Peregrinus A";
    }

    throw new Error("Invalid tone");
};
